/**
 * Shared helper for bulk-action handlers (delete, download, upload):
 * partition Promise.allSettled-style outcomes into success/failure lists
 * and produce the toast message + first failure error in one call.
 */

export type Outcome<T, E = unknown> =
  | { ok: true; value: T }
  | { ok: false; value: T; error: E };

interface SummaryArgs {
  successVerb: string; // e.g. 'Deleted', 'Downloaded', 'Uploaded'
  failureVerb: string; // e.g. 'delete', 'download', 'upload'
}

export interface OutcomeSummary<T, E = unknown> {
  succeeded: T[];
  failed: { value: T; error: E }[];
  /** Pre-formatted title for `toastFromError` — only meaningful when `failed.length > 0`. */
  failureTitle: string;
  /** Convenience: representative error to feed `toastFromError`. */
  firstError: E | null;
}

export const summarizeOutcomes = <T, E = unknown>(
  outcomes: Outcome<T, E>[],
  toName: (value: T) => string,
  { successVerb, failureVerb }: SummaryArgs,
): OutcomeSummary<T, E> => {
  const succeeded = outcomes.flatMap((o) => (o.ok ? [o.value] : []));
  const failed = outcomes.flatMap((o) =>
    o.ok ? [] : [{ value: o.value, error: o.error }],
  );
  if (failed.length === 0) {
    return { succeeded, failed, failureTitle: '', firstError: null };
  }
  const names = failed.map((f) => toName(f.value)).join(', ');
  const failureTitle =
    succeeded.length > 0
      ? `${successVerb} ${succeeded.length}, failed ${failed.length}: ${names}`
      : `Failed to ${failureVerb} ${names}`;
  return { succeeded, failed, failureTitle, firstError: failed[0].error };
};
